import { Card, CardActionArea, CardMedia, CardContent, Typography, Grid, Box } from "@mui/material";
import { useMemo } from "react";
import { StarRating } from "./StarRating";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { Favorite } from "@mui/icons-material";
import Theme from "../utils/theme";
import { capitalize } from "../utils/helpers/generalHelper";
import type { IRanking } from "../types/recipe";
import { getRankingAverage } from "../utils/helpers/recipeHelper";
import React from "react";

interface IThumbnailGridProps {
    items: any[];
    getImage: (item: any) => string;
    getLabel: (item: any) => string;
    onClick: (item: any) => void;
}

const ThumbnailGrid: React.FC<IThumbnailGridProps> = ({ items, getImage, getLabel, onClick }) => {
    const authState = useSelector((state: RootState) => state.auth);
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const renderItems = useMemo(
        () =>
            items.map((item, index) => {
                const filteredRanking = item.ranking?.filter((rank: IRanking) => rank.user.username === authState.account?.username);
                const starRatingValue = filteredRanking ? (filteredRanking[0] ? filteredRanking[0].value : 0) : 0;

                return (
                    <Grid
                        key={getImage(item) + index}
                        sx={{
                            width: { xs: "100%", sm: "40%", md: "320px" },
                            display: "flex",
                        }}
                    >
                        <Card
                            sx={{
                                borderRadius: "20px",
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                height: "100%",
                            }}
                        >
                            <CardActionArea
                                onClick={() => onClick(item)}
                                sx={{
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                    alignItems: "stretch",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        position: "absolute",
                                        alignItems: "center",
                                        gap: 0.5,
                                        backgroundColor: theme.palette.background.paper,
                                        border: "2px solid " + theme.palette.primary.main,
                                        borderRadius: 5,
                                        px: 1,
                                        py: 0.5,
                                        right: 5,
                                        top: 220,
                                    }}
                                >
                                    <Favorite sx={{ fontSize: 24, color: theme.palette.primary.main }} fontSize="small" />
                                    <Typography variant="subtitle2" sx={{ fontSize: 16 }}>
                                        {item.likes?.length}
                                    </Typography>
                                </Box>
                                <CardMedia
                                    component="img"
                                    height="240"
                                    image={getImage(item) || "/images/recipePlaceholder.png"}
                                    alt={getLabel(item)}
                                    sx={{ width: "100%" }}
                                />
                                <CardContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        flexGrow: 1,
                                    }}
                                >
                                    <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                                        <Typography variant="h6">{getLabel(item)}</Typography>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main }}>
                                            {item.user.username}
                                        </Typography>
                                        <Typography>{capitalize(item.visibility)}</Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-end",
                                            mt: 1,
                                        }}
                                    >
                                        <StarRating
                                            size={"lg"}
                                            objectId={item._id}
                                            average={item.ranking ? getRankingAverage(item.ranking) : 0}
                                            count={item.ranking?.length}
                                            value={starRatingValue ?? 0}
                                        />
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                );
            }),
        [authState.account?.username, getImage, getLabel, items, onClick, theme.palette.primary.main]
    );

    return (
        <Grid container spacing={2} sx={{ display: "flex", justifyContent: "center", px: { xs: 1, sm: 1, md: 0 } }}>
            {renderItems}
        </Grid>
    );
};

export default React.memo(ThumbnailGrid);
